import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isSupabaseConfigured } from './lib/supabase';
import Login from './pages/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Trucks from './pages/Trucks';
import TruckDetail from './pages/TruckDetail';
import Inventory from './pages/Inventory';
import ScrapGallery from './pages/ScrapGallery';
import History from './pages/History';
import Tutorial from './pages/Tutorial';
import SetupRequired from './pages/SetupRequired';
import SetupAdmin from './pages/SetupAdmin';
import Admin from './pages/Admin';
import { Toaster } from 'sonner';
import './App.css';

const LoadingScreen = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="text-zinc-900 font-mono">MEMUAT SISTEM...</div>
  </div>
);

const CustomerRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'admin') return <Navigate to="/admin" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role !== 'admin') return <Navigate to="/inventory" replace />;
  return children;
};

const AppRoutes = () => {
  const { adminExists, user, profile, loading } = useAuth();

  // Wait until we know if admin exists (and if user-session is resolved)
  if (adminExists === null || loading) return <LoadingScreen />;

  // First-time setup: no admin exists yet
  if (!adminExists) {
    return (
      <Routes>
        <Route path="*" element={<SetupAdmin />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/admin" element={
        <AdminRoute>
          <Admin />
        </AdminRoute>
      } />

      <Route path="/" element={
        <CustomerRoute>
          <Layout />
        </CustomerRoute>
      }>
        <Route index element={<Navigate to="/inventory" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="trucks" element={<Trucks />} />
        <Route path="trucks/:id" element={<TruckDetail />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="scrap" element={<ScrapGallery />} />
        <Route path="history" element={<History />} />
        <Route path="tutorial" element={<Tutorial />} />
      </Route>

      {/* Fallback: route based on auth state */}
      <Route path="*" element={
        user ? (
          profile?.role === 'admin' ? <Navigate to="/admin" replace /> : <Navigate to="/inventory" replace />
        ) : <Navigate to="/login" replace />
      } />
    </Routes>
  );
};

function App() {
  if (!isSupabaseConfigured) {
    return (
      <>
        <SetupRequired />
        <Toaster position="top-right" theme="light" />
      </>
    );
  }
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" theme="light" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
